import { logAdapterEvent, logAdapterDebug, logError } from "../../helper/logger";
import { getPackageIdsByType } from "../../helper/genre-helper";
import { getComparePkgTrayData } from "../../helper/ComparePkgTray";
import { FlipCardsCmsData } from "../../../src/components/video-lab/flip-cards/flip-cards.cms";
import { getMultiOfferDuration } from "../../../src/components/video-lab/plan-cards/utils";
import { DEFAULT_GEMINI_FEE } from "../../constant/epoch-constant";

const DTVI_IDS = [
  "PREMIER",
  "ULTIMATE",
  "CHOICE",
  "ENTERTAINMENT",
  "OPTIMO MAS"
] as const;
type DTViId = typeof DTVI_IDS[number];

const DTVS_IDS = [
  "PREMIER",
  "ULTIMATE",
  "CHOICE",
  "ENTERTAINMENT",
  "OPTIMO MAS",
  "LO MAXIMO",
  "MAS LATINO",
  "MAS ULTRA"
] as const;
type DTVsId = typeof DTVS_IDS[number];

const ADAPTER_NAME = "flipCardsAdapter";

// Legal text placeholder tokens
const OFFER_PRICE_PLACEHOLDER = '[offer_price]';
const REGULAR_PRICE_PLACEHOLDER = '[regular_price]';
const PROMO_DURATION_PLACEHOLDER = '[promo_duration]';
// Aliases matching the genre-pack-details-panel token vocabulary so the same legal copy works here.
const STRIKE_THROUGH_PRICE_PLACEHOLDER = '[strike_through_price]'; // alias of [regular_price]
const OFFER_DURATION_PLACEHOLDER = '[offer_duration]';             // alias of [promo_duration]

/**
 * @description
 * Resolve all known placeholders in legal text:
 *  - [offer_price]           → formatted offer price (e.g. "$34.99")
 *  - [regular_price]         → formatted regular price (e.g. "$54.99")
 *  - [strike_through_price]  → formatted regular price (alias; genre-pack-details-panel vocabulary)
 *  - [promo_duration]        → promo duration in months (e.g. "3")
 *  - [offer_duration]        → promo duration in months (alias; genre-pack-details-panel vocabulary)
 *
 * Returns null when any placeholder present cannot be resolved (hides unresolvable text).
 */
const resolveLegalPlaceholders = (
  text: string | null | undefined,
  offerPrice: string | null,
  regularPrice: string | null,
  promoDuration: string | null
): string | null => {
  if (text == null) {
    return null;
  }
  let result = text;

  if (result.includes(OFFER_PRICE_PLACEHOLDER)) {
    if (!offerPrice) {
      return null;
    }
    result = result.replace(new RegExp(OFFER_PRICE_PLACEHOLDER.replace(/[[\]]/g, '\\$&'), 'g'), offerPrice);
  }

  if (result.includes(REGULAR_PRICE_PLACEHOLDER)) {
    if (!regularPrice) {
      return null;
    }
    result = result.replace(new RegExp(REGULAR_PRICE_PLACEHOLDER.replace(/[[\]]/g, '\\$&'), 'g'), regularPrice);
  }

  if (result.includes(PROMO_DURATION_PLACEHOLDER)) {
    if (!promoDuration) {
      return null;
    }
    result = result.replace(new RegExp(PROMO_DURATION_PLACEHOLDER.replace(/[[\]]/g, '\\$&'), 'g'), promoDuration);
  }

  // Aliases (genre-pack-details-panel vocabulary): [strike_through_price] == regular price,
  // [offer_duration] == promo duration. Resolved against the same values.
  if (result.includes(STRIKE_THROUGH_PRICE_PLACEHOLDER)) {
    if (!regularPrice) {
      return null;
    }
    result = result.replace(new RegExp(STRIKE_THROUGH_PRICE_PLACEHOLDER.replace(/[[\]]/g, '\\$&'), 'g'), regularPrice);
  }

  if (result.includes(OFFER_DURATION_PLACEHOLDER)) {
    if (!promoDuration) {
      return null;
    }
    result = result.replace(new RegExp(OFFER_DURATION_PLACEHOLDER.replace(/[[\]]/g, '\\$&'), 'g'), promoDuration);
  }

  return result;
};

const resolveOfferTokens = (
  text: string,
  benefits: { discount: number; periodMo: number }[]
): string => {
  if (!benefits || benefits.length === 0) {
    return /\[[^\]]+\]/.test(text) ? "" : text;
  }
  const sorted = [...benefits]
    .filter(b => typeof b.discount === 'number' && typeof b.periodMo === 'number')
    .sort((a, b) => a.periodMo - b.periodMo);

  if (sorted.length === 0) {
    return text;
  }

  let resolved = text;

  if (sorted.length === 1) {
    const discountVal = Math.round(sorted[0].discount ?? 0);
    const durationMo = sorted[0].periodMo ?? 0;
    if (discountVal <= 0 || durationMo <= 0) {
      return "";
    }
    const durationText = getMultiOfferDuration(null, durationMo);
    resolved = resolved
      .replace(/\[first_offer\]/g, String(discountVal))
      .replace(/\[first_offer_duration\]/g, durationText);
    // Single offer: strip entire second-offer clauses so no orphaned boilerplate remains
    // (e.g. "; $89.99/mo. for months" from total_offers_prices_durations,
    //       "; off for mos." from benefits_prices_durations).
    // plan-card-adapter strips these at source before tokenisation; flip-cards-adapter
    // runs token cleanup only — without this, surrounding static text is left behind after
    // [second_offer]/[second_offer_duration] tokens are removed.
    resolved = resolved
      .replace(/;\s*\$\[second_req_price\][^);}\n]*/gi, "")
      .replace(/\s*;?\s*\$?\[second_offer\].*?\[second_offer_duration\]/gi, "");
  } else {
    const firstMonthDiscount = sorted.reduce((sum, b) => sum + (b.discount ?? 0), 0);
    const firstBenefitPeriod = sorted[0]?.periodMo ?? 1;
    resolved = resolved
      .replace(/\[first_offer\]/g, String(Math.round(firstMonthDiscount)))
      .replace(/\[first_offer_duration\]/g, getMultiOfferDuration(1, firstBenefitPeriod));

    const next = sorted[1];
    const discountVal = Math.round(next.discount ?? 0);
    const periodMo = next.periodMo ?? 0;
    if (discountVal > 0 && periodMo > firstBenefitPeriod) {
      resolved = resolved
        .replace(/\[second_offer\]/g, String(discountVal))
        .replace(/\[second_offer_duration\]/g, getMultiOfferDuration(firstBenefitPeriod + 1, periodMo));
    }
  }

  // Clean up any leftover offer tokens — remove only the token itself (+ optional
  // preceding $) to avoid truncating trailing sentence text on the same line.
  // Collapse the double-space / space-before-punctuation left behind by a mid-sentence
  // removal, then trim, so no spacing artifact reaches the rendered legal copy.
  resolved = resolved
    .replace(/\$?\[(?:[a-z_]*_offer)\]/gi, "")
    .replace(/\[[a-z_]*_offer_duration\]/gi, "")
    .replace(/\s+([.,;:])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return resolved;
};

/**
 * @description
 * Splits cents from priceText.content and prepends to spanText.content
 * e.g. content: "$34.99", spanText.content: "/mo." → content: "$34", spanText.content: ".99/mo."
 */
const splitCentsToSpanText = (priceText: any) => {
  if (!priceText?.content) {
    return priceText;
  }
  const match = priceText.content.match(/(\d+)\.(\d{1,2})/);
  if (!match) {
    return priceText;
  }
  const cents = `.${match[2]}`;
  const updatedContent = priceText.content.replace(`.${match[2]}`, "");
  const existingSpan = priceText?.spanText?.content || "";
  return {
    ...priceText,
    content: updatedContent,
    spanText: {
      ...priceText?.spanText,
      content: `${cents}${existingSpan}`
    }
  };
};

interface IFlipCardsAdapter {
  content: any;
  packageConfig?: any[];
  genreSvodOffers?: any;
  getOffersData?: any;
  getOffersDTVsData?: any;
  embeddedSvods?: any[];
  dtviEmbeddedSvodOffers?: any;
  dtvsEmbeddedSvodOffers?: any;
  byodPlanCardOffers?: any;
}

/**
 * Normalizes productType values from CMS to canonical internal codes.
 * Accepts packageConfig variant names (internet/satellite) as aliases so CMS authors
 * can use the same terminology across the page-level feed and packageConfig feed.
 */
const normalizeProductType = (pt: string): "DTVI" | "DTVS" | "BYOD" | "GENRE" | "" => {
  switch (pt?.toUpperCase()) {
    case 'INTERNET': return 'DTVI';
    case 'DTVI': return 'DTVI';
    case 'SATELLITE': return 'DTVS';
    case 'DTVS': return 'DTVS';
    case 'BYOD': return 'BYOD';
    case 'GENRE': return 'GENRE';
    default: {
      if (pt) {
        logError(ADAPTER_NAME, new Error(`Unrecognized productType "${pt}" — card may be mispriced`), {
          step: 'normalizeProductType',
          value: pt
        });
      }
      return '';
    }
  }
};

/**
 * @description
 * Get package legal text from packageConfig for signature cards (DTVI/DTVS/BYOD).
 * Returns the combined legal text from packageConfig.offers[0].{internet|satellite|byod}.packageLegal
 * or null if not available.
 */
const getPackageLegalFromConfig = (
  cardId: string,
  packageConfig: any[],
  productType: string
): { benefitsPrices: string | null; totalOffersPrices: string | null; genreLegalText: string | null } => {
  try {
    if (!Array.isArray(packageConfig) || !cardId) {
      return {
        benefitsPrices: null,
        totalOffersPrices: null,
        genreLegalText: null
      };
    }

    const packageItem = packageConfig.find(pkg => pkg?.id === cardId);
    if (!packageItem?.offers?.[0]) {
      return {
        benefitsPrices: null,
        totalOffersPrices: null,
        genreLegalText: null
      };
    }

    const offerVariants = packageItem.offers[0];
    let variant: any = null;

    if (productType?.toUpperCase() === 'DTVS') {
      variant = offerVariants?.satellite;
    } else if (productType?.toUpperCase() === 'BYOD') {
      variant = offerVariants?.byod;
    } else if (productType?.toUpperCase() === 'GENRE') {
      variant = offerVariants?.genre;
    } else {
      variant = offerVariants?.internet;
    }

    const packageLegal = variant?.packageLegal;
    return {
      benefitsPrices: packageLegal?.benefits_prices_durations || null,
      totalOffersPrices: packageLegal?.total_offers_prices_durations || null,
      genreLegalText: packageLegal?.genre_legal_text || null
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, { step: "getPackageLegalFromConfig" });
    return {
      benefitsPrices: null,
      totalOffersPrices: null,
      genreLegalText: null
    };
  }
};

/**
 * @description
 * Get package details from packageConfig based on card config id
 * Falls back to card.config values when packageConfig is not available
 */
const getPackageDetails = (cards: any[], packageConfig: any[]) => {
  try {
    if (!Array.isArray(packageConfig) || !Array.isArray(cards)) {
      return {};
    }
    const packageIds = cards?.map(card => card?.config?.id);
    const packageMapping = {};
    packageConfig?.forEach(pkg => {
      if (pkg?.id && packageIds.includes(pkg?.id)) {
        packageMapping[pkg.id] = {
          packageName: pkg?.displayLabel,
          channelCount: Array.isArray(pkg?.channel) ? pkg.channel[0]?.displayCount : pkg?.channel?.displayCount,
          fallBackPrice: pkg?.price,
          queryparam: pkg?.queryparam || null
        };
      }
    });
    return packageMapping;
  } catch (error) {
    logError(ADAPTER_NAME, error, { step: "getPackageDetails" });
    return {};
  }
};

/**
 * @description
 * Get epoch id from card id based on productType.
 * DTVI/BYOD strips ATT_TV_ prefix, DTVS strips DTV_TV_ / DTV_ prefix, GENRE returns as-is.
 * joinChar controls how segments are joined (empty string for SVOD lookups, space for pricing).
 */
const getEpochId = (productType: string, cardId: string, joinChar = " ") => {
  switch (productType?.toUpperCase()) {
    case "DTVI":
    case "BYOD":
      return cardId
        ?.replace(/^ATT_TV_/, "")
        ?.split("_")
        ?.join(joinChar);
    case "DTVS":
      return cardId
        ?.replace(/^DTV_TV_/, "")
        ?.replace(/^DTV_/, "")
        ?.split("_")
        ?.join(joinChar);
    default:
      return cardId;
  }
};

/**
 * @description
 * Computes discounted pricing for a single offerPrices entry (shared by DTVI & BYOD).
 * Returns a record mapping package name → { price, regularPrice, promoDuration } or raw price.
 */
const computeDiscountedPrices = (curr: any, getPriceFn: (curr: any, key: string) => any): Record<string, any> => {
  const result: Record<string, any> = {};
  const benefits = curr?.benefits || [];
  const promos = curr?.promosOnSelection || [];
  const promosSet = new Set(promos.map((p: any) => p?.toString?.().toUpperCase?.()).filter(Boolean));
  // If benefits exist but promosOnSelection is empty, the resolver pre-filtered them — use benefits directly
  const applicableBenefits = promosSet.size > 0 ?
    benefits.filter((b: any) => promosSet.has(b?.code?.toString?.().toUpperCase?.())) :
    benefits;
  const totalDiscount = applicableBenefits.reduce((sum: number, b: any) => sum + (Number(b?.discount) || 0), 0);
  const maxPeriodMo = applicableBenefits.reduce((max: number, b: any) => Math.max(max, Number(b?.periodMo) || 0), 0);
  const sortedBenefits = [...applicableBenefits]
    .map((b: any) => ({
      discount: Number(b?.discount) || 0,
      periodMo: Number(b?.periodMo) || 0
    }))
    .sort((a, b) => a.periodMo - b.periodMo);
  Object.keys(curr || {}).forEach(key => {
    if (DTVI_IDS.includes(key as DTViId)) {
      const price = getPriceFn(curr, key);
      if (totalDiscount > 0 && price != null) {
        result[key] = {
          price,
          regularPrice: Math.round((Number(price) + totalDiscount) * 100) / 100,
          promoDuration: maxPeriodMo || null,
          benefits: sortedBenefits
        };
      } else {
        result[key] = price;
      }
    }
  });
  return result;
};

/**
 * @description
 * Get dynamic pricing for flip cards based on productType.
 * Supports GENRE (genreSvodOffers), DTVI (getOffersData), DTVS (getOffersDTVsData), BYOD (getOffersData).
 */
const getDynamicPricing = (args: {
  productType: string;
  cards: any[];
  genreSvodOffers: any;
  getOffersData: any;
  getOffersDTVsData: any;
  packageConfig: any[];
  byodPlanCardOffers?: any;
}) => {
  const { productType, cards, genreSvodOffers, getOffersData, getOffersDTVsData, packageConfig, byodPlanCardOffers } = args;
  try {
    let pricesReduced: Record<string, any> = {};

    switch (productType?.toUpperCase()) {
      case "GENRE": {
        if (!genreSvodOffers?.offerPrices) {
          return {};
        }
        const genreIds = getPackageIdsByType({
          type: "genre",
          packageConfig
        });
        const genreMiniIds = getPackageIdsByType({
          type: "genre-mini",
          packageConfig
        });
        const GENRE_IDS = [...genreIds, ...genreMiniIds];
        pricesReduced = genreSvodOffers.offerPrices.reduce((acc, curr) => {
          if (!curr) {
            return acc;
          }
          Object.keys(curr || {}).forEach(key => {
            if (GENRE_IDS.includes(key)) {
              acc[key] = {
                price: curr[key]?.price ?? null,
                regularPrice: curr[key]?.regularPrice ?? null,
                promoDuration: curr[key]?.promoDuration ?? null
              };
            }
          });
          return acc;
        }, {});
        break;
      }
      case "DTVI": {
        pricesReduced = getOffersData?.offerPrices?.reduce((acc, curr) => {
          if (!curr) {
            return acc;
          }
          return {
            ...acc,
            ...computeDiscountedPrices(curr, (c, key) => c[key])
          };
        }, {}) || {};
        break;
      }
      case "DTVS": {
        pricesReduced = getOffersDTVsData?.offerPrices?.reduce((acc, curr) => {
          if (!curr) {
            return acc;
          }
          Object.keys(curr || {}).forEach(key => {
            if (DTVS_IDS.includes(key as DTVsId)) {
              acc[key] = curr?.localPrice?.[key] || null;
            }
          });
          return acc;
        }, {}) || {};
        break;
      }
      case "BYOD": {
        // Mirror plan-card-adapter: BYOD has its own promo feed (byodPlanCardOffers) whose
        // promos differ from the DTVI feed, so prefer it when present. Its items carry the
        // price directly under the package key; getOffersData nests it under `byodPrice`.
        const useByodFeed = !!byodPlanCardOffers?.offerPrices;
        const byodSource = useByodFeed ? byodPlanCardOffers : getOffersData;
        const byodGetter = useByodFeed ?
          (c: any, key: string) => c?.[key] :
          (c: any, key: string) => c?.byodPrice?.[key];
        pricesReduced = byodSource?.offerPrices?.reduce((acc, curr) => {
          if (!curr) {
            return acc;
          }
          return {
            ...acc,
            ...computeDiscountedPrices(curr, byodGetter)
          };
        }, {}) || {};
        break;
      }
      default:
        break;
    }

    const priceMapping = {};
    if (!Array.isArray(cards)) {
      return priceMapping;
    }
    cards.forEach(card => {
      const cardId = card?.config?.id;
      const epochId = getEpochId(productType, cardId);
      const val = pricesReduced?.[epochId];
      const priceVal = typeof val === "object" && val !== null ? val?.price : val;
      const regularVal = typeof val === "object" && val !== null ? val?.regularPrice : undefined;
      const promoDurationVal = typeof val === "object" && val !== null ? val?.promoDuration : undefined;
      const benefitsVal = typeof val === "object" && val !== null ? val?.benefits : undefined;
      priceMapping[cardId] = {
        dynamicPricing: priceVal != null && Number(priceVal) >= 0 ? String(priceVal) : null,
        ...(regularVal != null && regularVal !== undefined && priceVal !== regularVal ?
          { regularPrice: regularVal != null && Number(regularVal) >= 0 ? String(regularVal) : null } :
          {}),
        ...(promoDurationVal != null ? { promoDuration: promoDurationVal } : {}),
        ...(benefitsVal ? { benefits: benefitsVal } : {})
      };
    });
    return priceMapping;
  } catch (error) {
    logError(ADAPTER_NAME, error, { step: "getDynamicPricing" });
    return {};
  }
};

/**
 * @description
 * Get dynamic SVODs for flip cards based on productType.
 * GENRE: uses genreSvodOffers.svodMapping + embeddedSvods (supports multiple SVODs sharing same offerCode)
 * DTVI/BYOD: uses dtviEmbeddedSvodOffers + embeddedSvods (flipCardsData.icon)
 * DTVS: uses dtvsEmbeddedSvodOffers + embeddedSvods via offerCodeSatellite (flipCardsData.icon)
 */
const buildSvodLookupMap = (svods: any[], key: string): Record<string, any[]> => {
  const map: Record<string, any[]> = {};
  svods?.forEach(item => {
    const k = item?.[key];
    if (k) {
      if (!map[k]) {
        map[k] = [];
      }
      map[k].push(item);
    }
  });
  return map;
};

const getDynamicSvods = (args: {
  productType: string;
  cards: any[];
  genreSvodOffers: any;
  embeddedSvods: any[];
  dtviEmbeddedSvodOffers?: any;
  dtvsEmbeddedSvodOffers?: any;
}) => {
  const { productType, cards, genreSvodOffers, embeddedSvods, dtviEmbeddedSvodOffers, dtvsEmbeddedSvodOffers } = args;
  try {
    const svodsMapping = {};
    if (!Array.isArray(cards)) {
      return svodsMapping;
    }

    switch (productType?.toUpperCase()) {
      case "GENRE": {
        if (!genreSvodOffers?.svodMapping) {
          return {};
        }

        const embeddedSvodsMap = buildSvodLookupMap(embeddedSvods, 'offerCode');

        cards.forEach(card => {
          const cardId = card?.config?.id;
          const svodMapping = genreSvodOffers.svodMapping?.[cardId];

          const svodsReduced =
            svodMapping
              ?.reduce((acc, curr) => {
                // curr is a string offerCode, e.g. "BOLT-DUOBASIC-202411"
                // Expand every embedded svod that shares the same offerCode so duplicates (e.g. Disney+/Hulu) render separately.
                const matchingSvods = embeddedSvodsMap[curr] || [];

                if (matchingSvods.length > 0) {
                  matchingSvods.forEach(embeddedSvodMatch => {
                    const flipData = embeddedSvodMatch?.flipCardsData;
                    if (flipData?.disable) {
                      return;
                    }
                    const svod = {
                      code: curr,
                      name:
                        embeddedSvodMatch?.displayName ||
                        genreSvodOffers?.svodNameMapping?.[curr] ||
                        embeddedSvodMatch?.name ||
                        null,
                      logo: flipData?.icon || null
                    };
                    acc.push(svod);
                  });
                  return acc;
                }

                // No embedded svod match — skip (will be filtered out by hasName/hasLogo anyway)
                return acc;
              }, [])
              ?.filter(svod => {
                const hasName = !!svod?.name;
                const hasLogo = !!svod?.logo;
                const isExcluded = card?.excludedSvods?.includes(svod?.code);
                return hasName && hasLogo && !isExcluded;
              }) || [];

          svodsMapping[cardId] = { svods: svodsReduced };
        });
        break;
      }
      case "DTVI":
      case "BYOD": {
        const dtviEmbeddedSvodsMap = buildSvodLookupMap(embeddedSvods, 'offerCode');

        cards.forEach(card => {
          const cardId = card?.config?.id;
          const epochId = getEpochId(productType, cardId, "");
          const svodsReduced =
            dtviEmbeddedSvodOffers?.[epochId?.toLowerCase()]
              ?.reduce((acc, curr) => {
                const matchingEmbeddedSvods = dtviEmbeddedSvodsMap[curr?.code] || [];
                matchingEmbeddedSvods.forEach(embeddedSvod => {
                  const flipData = embeddedSvod?.flipCardsData;
                  if (flipData?.disable) {
                    return;
                  }
                  const svod = {
                    code: curr?.code || null,
                    name: embeddedSvod?.displayName || embeddedSvod?.name || curr?.name || null,
                    logo: flipData?.icon || embeddedSvod?.packageCarouselData?.icon || null
                  };
                  acc.push(svod);
                });
                return acc;
              }, [])
              ?.filter(
                svod => svod?.name &&
                  svod?.logo &&
                  !card?.excludedSvods?.includes(svod?.code)
              ) || [];
          svodsMapping[cardId] = { svods: svodsReduced };
        });
        break;
      }
      case "DTVS": {
        const dtvsEmbeddedSvodsMap = buildSvodLookupMap(embeddedSvods, 'offerCodeSatellite');

        cards.forEach(card => {
          const cardId = card?.config?.id;
          const epochId = getEpochId(productType, cardId, "");
          const svodsReduced =
            dtvsEmbeddedSvodOffers?.[epochId?.toLowerCase()]
              ?.reduce((acc, curr) => {
                const matchingEmbeddedSvods = dtvsEmbeddedSvodsMap[curr?.code] || [];
                matchingEmbeddedSvods.forEach(embeddedSvod => {
                  const flipData = embeddedSvod?.flipCardsData;
                  if (flipData?.disable) {
                    return;
                  }
                  const svod = {
                    code: curr?.code || null,
                    name: embeddedSvod?.displayName || embeddedSvod?.name || curr?.name || null,
                    logo: flipData?.icon || embeddedSvod?.packageCarouselData?.icon || null
                  };
                  acc.push(svod);
                });
                return acc;
              }, [])
              ?.filter(
                svod => svod?.name &&
                  svod?.logo &&
                  !card?.excludedSvods?.includes(svod?.code)
              ) || [];
          svodsMapping[cardId] = { svods: svodsReduced };
        });
        break;
      }
      default:
        break;
    }
    return svodsMapping;
  } catch (error) {
    logError(ADAPTER_NAME, error, { step: "getDynamicSvods" });
    return {};
  }
};

/**
 * @description
 * Inject dynamic content into flip cards:
 * - Package names from packageConfig (replaces [package_name])
 * - Channel counts from packageConfig (replaces [channel_count])
 * - Dynamic pricing (replaces [offer_price], [regular_price], [promo_duration])
 *   Sources: genreSvodOffers (GENRE), getOffersData (DTVI/BYOD), getOffersDTVsData (DTVS)
 * - Dynamic SVODs
 *   Sources: genreSvodOffers + embeddedSvods (GENRE), dtviEmbeddedSvodOffers (DTVI/BYOD), dtvsEmbeddedSvodOffers (DTVS)
 *
 * Falls back to card.config values (packageName, channelCount, fallBackPrice) when
 * dynamic data sources are not available.
 */
const injectDynamicContent = ({ cards, packageConfig, genreSvodOffers, getOffersData, getOffersDTVsData, embeddedSvods, productType, dtviEmbeddedSvodOffers, dtvsEmbeddedSvodOffers, byodPlanCardOffers }: any) => {
  try {
    const dynamicPackageData = getPackageDetails(cards, packageConfig);

    // Device fee for "with required fees" pricing (DTVI/DTVS only, BYOD = 0)
    // RSN fee is NOT added here — it's zip-dependent and hydrated client-side (same as plan-card-adapter)
    const rawGeminiFee = parseFloat(getOffersData?.geminiFee);
    const geminiFee = !isNaN(rawGeminiFee) ? rawGeminiFee : DEFAULT_GEMINI_FEE;
    if (isNaN(rawGeminiFee)) {
      // geminiFee is currently absent from the live DTVI feed (upstream device-key mismatch in
      // epoch-dtvi-offers.resolver — tracked separately), so this is the normal case today, not
      // an exception. Log at debug to avoid error-level spam on every adapt. The default still
      // feeds [gemini_amount]/required-price legal copy; the durable fix is the device key.
      logAdapterDebug(ADAPTER_NAME, "geminiFee missing from feed — using default", {
        step: 'injectDynamicContent:geminiFee',
        defaultGeminiFee: DEFAULT_GEMINI_FEE
      });
    }
    const RSN_EXCLUDED_PACKAGES = ["ENTERTAINMENT", "MÁS LATINO", "MAS LATINO", "ÓPTIMO MÁS", "OPTIMO MAS"];

    // Main pricing for component-level productType
    const dynamicPricing = getDynamicPricing({
      productType,
      cards,
      genreSvodOffers,
      getOffersData,
      getOffersDTVsData,
      packageConfig,
      byodPlanCardOffers
    });

    // Per-card productType overrides: compute additional pricing for cards with their own productType.
    // Compare and group on the NORMALIZED productType — a card config of "internet" maps to "DTVI",
    // which must match the (already-normalized) component productType so it is not falsely treated
    // as an override and re-priced under an unrecognized type (which would return null prices).
    const overrideCards = cards?.filter(card => {
      if (!card?.config?.productType) {
        return false;
      }
      const normalized = normalizeProductType(card.config.productType);
      // Exclude unknown types (normalize to ""): they'd form an empty override group whose
      // null pricing would Object.assign-clobber the card's valid container-level pricing,
      // silently degrading it to the static fallback on a regulated-pricing surface. Such a
      // card instead keeps container pricing via the `|| productType` fallback below.
      return normalized !== "" && normalized !== productType?.toUpperCase();
    }) || [];
    if (overrideCards.length > 0) {
      const overrideGroups: Record<string, any[]> = {};
      overrideCards.forEach(card => {
        const pt = normalizeProductType(card.config.productType);
        if (!overrideGroups[pt]) {
          overrideGroups[pt] = [];
        }
        overrideGroups[pt].push(card);
      });
      Object.entries(overrideGroups).forEach(([pt, groupCards]) => {
        const overridePricing = getDynamicPricing({
          productType: pt,
          cards: groupCards,
          genreSvodOffers,
          getOffersData,
          getOffersDTVsData,
          packageConfig,
          byodPlanCardOffers
        });
        Object.assign(dynamicPricing, overridePricing);
      });
    }

    // Main SVODs for component-level productType
    const dynamicSvods = getDynamicSvods({
      productType,
      cards,
      genreSvodOffers,
      embeddedSvods,
      dtviEmbeddedSvodOffers,
      dtvsEmbeddedSvodOffers
    });

    // Per-card SVOD overrides
    if (overrideCards.length > 0) {
      const overrideGroups: Record<string, any[]> = {};
      overrideCards.forEach(card => {
        const pt = normalizeProductType(card.config.productType);
        if (!overrideGroups[pt]) {
          overrideGroups[pt] = [];
        }
        overrideGroups[pt].push(card);
      });
      Object.entries(overrideGroups).forEach(([pt, groupCards]) => {
        const overrideSvods = getDynamicSvods({
          productType: pt,
          cards: groupCards,
          genreSvodOffers,
          embeddedSvods,
          dtviEmbeddedSvodOffers,
          dtvsEmbeddedSvodOffers
        });
        Object.assign(dynamicSvods, overrideSvods);
      });
    }

    return cards?.map(card => {
      const cardId = card?.config?.id;
      if (!cardId) {
        // Static tiles have no pricing to inject, but Storyblok still delivers legalText as a
        // blok array. Normalize array→object so FrontCard/BackCard can read .content.
        const unwrapBlok = (val: any) => (Array.isArray(val) ? (val[0] ?? null) : (val ?? null));
        // Static tiles get no token resolution (no cardId → no pricing / packageConfig lookup),
        // so any leftover [token] is unresolvable here. Drop the field rather than leak a raw
        // template into AC-2 regulated legal copy — mirrors the dynamic path's safety guarantee.
        const sanitizeStaticLegal = (val: any) => {
          const obj = unwrapBlok(val);
          if (obj?.content && /\[[a-z_]+\]/i.test(obj.content)) {
            logError(ADAPTER_NAME, new Error('Static-tile legal text dropped — unresolved tokens remained'), {
              step: 'staticTileLegal',
              remainingTokens: (obj.content.match(/\[[a-z_]+\]/gi) || []).join(', ')
            });
            return null;
          }
          return obj;
        };
        const normFace = (face: any) => (face ? {
          ...face,
          legalText: sanitizeStaticLegal(face.legalText),
          legalTextNoOffer: sanitizeStaticLegal(face.legalTextNoOffer)
        } : face);
        return {
          ...card,
          frontCard: normFace(card.frontCard),
          backCard: normFace(card.backCard)
        };
      } // skip dynamic-pricing injection for cards without config id

      const pkg = dynamicPackageData?.[cardId];
      const cp = dynamicPricing?.[cardId];

      // Effective productType: per-card override or component-level default (both normalized)
      const cardProductType = normalizeProductType(card?.config?.productType) || productType;

      // Use dynamic data or fallback to card.config values
      const packageName = pkg?.packageName || card?.config?.packageName || "";
      const channelCount = pkg?.channelCount || card?.config?.channelCount || "";
      const price = cp?.dynamicPricing ?? pkg?.fallBackPrice ?? card?.config?.fallBackPrice ?? "";
      const hasPromo = !!(cp?.regularPrice && cp?.dynamicPricing && cp.dynamicPricing !== cp.regularPrice);

      // Resolve promo duration
      let resolvedDuration: string | undefined = undefined;
      if (hasPromo && cp?.promoDuration != null) {
        resolvedDuration = String(cp.promoDuration);
      }

      // "With required fees" pricing for legal text
      // Server-side: base + geminiFee only (NO RSN — RSN is zip-dependent, hydrated client-side)
      // Mirrors plan-card-adapter two-phase approach: SSR computes basePriceExcludingRsn,
      // client adds zip-specific rsnFee via useRsnFee() from ApplicationContext.
      const isSignatureCard = ['DTVI', 'DTVS'].includes(cardProductType?.toUpperCase());
      const deviceFee = isSignatureCard ? geminiFee : 0;
      const epochName = getEpochId(cardProductType, cardId);
      const cardUseRsnFee = isSignatureCard && !RSN_EXCLUDED_PACKAGES.some(name => epochName?.toUpperCase()?.includes(name));
      const firstMonthPrice = price ? parseFloat(price) + deviceFee : 0;
      const legalOfferPrice = firstMonthPrice ? firstMonthPrice.toFixed(2) : "";

      // Months 2+: (regularPrice + deviceFee) - ongoingDiscount
      // Benefits stack for month 1 but only the longest-running one applies after.
      // benefits are sorted by periodMo ascending; the last entry is the ongoing discount.
      const benefits = cp?.benefits || [];
      let legalRegularPrice = "";
      if (cp?.regularPrice && deviceFee > 0 && benefits.length > 0) {
        const fullPriceWithFees = parseFloat(cp.regularPrice) + deviceFee;
        const ongoingDiscount = benefits[benefits.length - 1]?.discount || 0;
        const secondMonthPrice = fullPriceWithFees - ongoingDiscount;
        legalRegularPrice = secondMonthPrice.toFixed(2);
      } else if (cp?.regularPrice) {
        legalRegularPrice = (parseFloat(cp.regularPrice) + deviceFee).toFixed(2);
      }

      // Helper to resolve priceText (front and back share same logic)
      const resolvePriceText = (priceText: any) => {
        if (!priceText) {
          return priceText;
        }
        let resolvedContent = priceText?.content?.replace?.("[offer_price]", price);
        // CMS may embed [promo_duration] directly in content (not only in the duration field).
        // Replace when resolved; strip the token otherwise so no raw placeholder is shown.
        if (resolvedContent?.includes?.('[promo_duration]')) {
          resolvedContent = hasPromo && resolvedDuration ?
            resolvedContent.replace(/\[promo_duration\]/g, resolvedDuration) :
            resolvedContent.replace(/\[promo_duration\]/g, '');
        }
        // Resolve [promo_duration] in spanText.content — CMS may store promo suffix there.
        // Storyblok delivers spanText as a blok array; unwrap to an object so .content is
        // readable (otherwise splitCentsToSpanText drops the "/mo. +tax*" suffix).
        let resolvedSpanText = Array.isArray(priceText?.spanText) ? priceText.spanText[0] : priceText?.spanText;
        if (resolvedSpanText?.content?.includes?.('[promo_duration]')) {
          resolvedSpanText = {
            ...resolvedSpanText,
            content: hasPromo && resolvedDuration ?
              resolvedSpanText.content.replace(/\[promo_duration\]/g, resolvedDuration) :
              resolvedSpanText.content.replace(/\[promo_duration\]/g, '')
          };
        }
        return {
          ...priceText,
          content: resolvedContent,
          spanText: resolvedSpanText,
          // Always set `duration` (don't rely on the `...priceText` spread) so a CMS-cleared value
          // ("" / whitespace) is overridden to undefined rather than carried through — otherwise
          // PricingSection's `duration ?? spanText` would render an empty suffix and swallow the
          // cents/"/mo." span. A non-empty duration (e.g. genre "for N mos.") resolves as before.
          duration: priceText?.duration?.trim() && hasPromo && resolvedDuration ?
            priceText.duration.replace("[promo_duration]", resolvedDuration) :
            undefined
        };
      };

      // True when CMS authored a [regular_price] placeholder — signals dynamic-pricing intent.
      // Faces without the placeholder (e.g. static "Starting at") opt out of promo-driven hide/show.
      const isDynamicStrike = (stp: any) => !!stp?.content?.includes?.("[regular_price]");

      // Helper to resolve strikeThroughPrice
      // - No placeholder → return CMS content untouched (static text like "Starting at").
      // - hasPromo → inject regularPrice. Otherwise → blank content so component hides it.
      const resolveStrikeThrough = (stp: any) => {
        if (!stp || !isDynamicStrike(stp)) {
          return stp;
        }
        return hasPromo ?
          {
            ...stp,
            content: stp?.content?.replace?.("[regular_price]", cp?.regularPrice || "")
          } :
          {
            ...stp,
            content: undefined
          };
      };

      // Helper to resolve offerRibbon — visibility mirrors the dynamic strike-through state.
      // Only acts when the same face has a [regular_price] placeholder (CMS opted in to coupling).
      const resolveOfferRibbon = (ribbon: any, dynamicStrikeOnFace: boolean) => {
        if (!ribbon || !dynamicStrikeOnFace) {
          return ribbon;
        }
        return hasPromo ? ribbon : {
          ...ribbon,
          content: undefined
        };
      };

      const frontIsDynamic = isDynamicStrike(card?.frontCard?.strikeThroughPrice);
      const backIsDynamic = isDynamicStrike(card?.backCard?.strikeThroughPrice);
      /**
       * Resolve legalText for a card face.
       * Priority:
       *  1. Card-level legalText/legalTextNoOffer (CMS) — selected by hasPromo
       *  2. null (packageConfig is used only to resolve placeholder tokens inside CMS content)
       *
       * Supported placeholders: [offer_price], [regular_price], [promo_duration].
       * Any remaining unresolved [token] patterns (e.g. packageConfig's $[first_offer]) are
       * treated as unresolvable — the entire text is hidden to avoid showing raw templates.
       */
      // Storyblok returns blok fields as arrays; normalize to a plain object so the rest
      // of the resolver can read .content and .cta without branching everywhere.
      const normalizeLegalField = (val: any): any => {
        const obj = Array.isArray(val) ? val[0] : val;
        if (!obj) {
          return obj;
        }
        return {
          ...obj,
          cta: Array.isArray(obj.cta) ? obj.cta[0] : obj.cta
        };
      };

      const resolveLegalTextForFace = (cardFace: any): any => {
        if (!cardFace) {
          return null;
        }

        // Step 1: Pick legalText or legalTextNoOffer from page-level CMS based on promo state.
        // Storyblok blok fields arrive as arrays — normalizeLegalField unwraps [0] and its nested cta.
        const cmsLegalText = normalizeLegalField(cardFace?.legalText);
        const cmsLegalTextNoOffer = normalizeLegalField(cardFace?.legalTextNoOffer);
        const selectedLegalText = hasPromo ? cmsLegalText : (cmsLegalTextNoOffer || cmsLegalText);

        if (!selectedLegalText?.content) {
          return null;
        }

        // 2a: packageConfig token replacement
        // [total_offers_prices_durations] / [benefits_prices_durations] — internet/BYOD/DTVS
        // [genre_legal_text] — genre packs (offers[0].genre.packageLegal.genre_legal_text)
        // Presence of a token drives the lookup; no product-type flag needed.
        let resolvedContent = selectedLegalText.content;
        if (/\[(total_offers_prices_durations|benefits_prices_durations|genre_legal_text)\]/i.test(resolvedContent) && packageConfig) {
          const pkgLegal = getPackageLegalFromConfig(cardId, packageConfig, cardProductType);
          resolvedContent = resolvedContent
            .replace(/\[total_offers_prices_durations\]/g, pkgLegal.totalOffersPrices || '')
            .replace(/\[benefits_prices_durations\]/g, pkgLegal.benefitsPrices || '')
            .replace(/\[genre_legal_text\]/g, pkgLegal.genreLegalText || '')
            .replace(/\s{2,}/g, ' ')
            .trim();
        }

        // 2b: [offer_price], [regular_price], [promo_duration], [strike_through_price], [offer_duration]
        resolvedContent = resolveLegalPlaceholders(
          resolvedContent,
          legalOfferPrice || null,
          legalRegularPrice || null,
          resolvedDuration || null
        );

        if (resolvedContent === null) {
          // legalText had price tokens that couldn't resolve; try legalTextNoOffer as fallback
          if (cmsLegalTextNoOffer?.content) {
            const noOfferResolved = resolveLegalPlaceholders(cmsLegalTextNoOffer.content, legalOfferPrice || null, legalRegularPrice || null, resolvedDuration || null);
            if (noOfferResolved && !/\[[a-z_]+\]/i.test(noOfferResolved)) {
              return {
                ...cmsLegalTextNoOffer,
                content: noOfferResolved
              };
            }
          }
          return null;
        }

        // 2c: [first_offer], [second_offer], [first_offer_duration], [second_offer_duration]
        if (/\[(first_offer|second_offer|first_offer_duration|second_offer_duration)\]/i.test(resolvedContent)) {
          resolvedContent = resolveOfferTokens(resolvedContent, benefits);
        }

        // 2d: [first_req_price], [second_req_price] — base price incl. geminiFee (no RSN).
        // Non-RSN cards: resolve server-side now. RSN cards: leave the token so
        // FlipCard.applyRsnToFace adds the zip-specific RSN client-side (two-phase, same as
        // [total_price]); the base offer/regular price is carried in _legalRsnMeta for that.
        if (/\[first_req_price\]/.test(resolvedContent) && legalOfferPrice && !cardUseRsnFee) {
          resolvedContent = resolvedContent.replace(/\[first_req_price\]/g, legalOfferPrice);
        }
        if (/\[second_req_price\]/.test(resolvedContent) && legalRegularPrice && !cardUseRsnFee) {
          resolvedContent = resolvedContent.replace(/\[second_req_price\]/g, legalRegularPrice);
        }

        // 2e: [gemini_amount] — always known server-side
        if (resolvedContent.includes('[gemini_amount]')) {
          resolvedContent = resolvedContent.replace(/\[gemini_amount\]/g, String(Math.round(geminiFee)));
        }
        // [total_price] for non-RSN cards: resolve server-side (price + gemini, no RSN)
        // For RSN cards: left as-is and resolved client-side in FlipCard.tsx applyRsnToFace
        if (resolvedContent.includes('[total_price]') && !cardUseRsnFee && legalOfferPrice) {
          resolvedContent = resolvedContent.replace(/\[total_price\]/g, legalOfferPrice);
        }

        // Hide content if any tokens remain unresolved — never show raw template to users.
        // [total_price]/[rsn_fee]/[first_req_price]/[second_req_price] are intentionally left for
        // client-side resolution on RSN cards only — exclude them from the safety check for RSN cards.
        const checkContent = cardUseRsnFee ?
          resolvedContent.replace(/\[total_price\]|\[rsn_fee\]|\[first_req_price\]|\[second_req_price\]/g, '') :
          resolvedContent;
        if (!resolvedContent || /\[[a-z_]+\]/i.test(checkContent)) {
          logError(ADAPTER_NAME, new Error('Legal text dropped — unresolved tokens remained after all resolution steps'), {
            step: 'resolveLegalTextForFace',
            remainingTokens: (checkContent.match(/\[[a-z_]+\]/gi) || []).join(', ')
          });
          return null;
        }

        return {
          ...selectedLegalText,
          content: resolvedContent
        };
      };

      const frontLegal = resolveLegalTextForFace(card?.frontCard);
      const backLegal = resolveLegalTextForFace(card?.backCard);

      return {
        ...card,
        config: {
          ...card?.config,
          _legalRsnMeta: cardUseRsnFee ? {
            useRsnFee: true,
            legalOfferPrice: legalOfferPrice || null,
            legalRegularPrice: legalRegularPrice || null
          } : undefined
        },
        frontCard: card?.frontCard ?
          {
            ...card.frontCard,
            title: {
              ...card?.frontCard?.title,
              content: card?.frontCard?.title?.content?.replace?.("[package_name]", packageName)
            },
            ...(card?.frontCard?.subHeading?.content?.includes?.("[channel_count]") && {
              subHeading: {
                ...card.frontCard.subHeading,
                content: card.frontCard.subHeading.content.replace(/\[channel_count\]/g, channelCount)
              }
            }),
            ...(card?.frontCard?.strikeThroughPrice && {
              strikeThroughPrice: resolveStrikeThrough(card?.frontCard?.strikeThroughPrice)
            }),
            ...(card?.frontCard?.offerRibbon && {
              offerRibbon: resolveOfferRibbon(card?.frontCard?.offerRibbon, frontIsDynamic)
            }),
            priceText: splitCentsToSpanText(resolvePriceText(card?.frontCard?.priceText)),
            legalText: frontLegal
          } :
          undefined,
        backCard: card?.backCard ?
          {
            ...card.backCard,
            title: {
              ...card?.backCard?.title,
              content: card?.backCard?.title?.content?.replace?.("[package_name]", packageName)
            },
            ...(card?.backCard?.subHeading && (() => {
              const sh = card.backCard.subHeading;
              // Normalize cta: Storyblok delivers blok fields as arrays
              const rawCta = Array.isArray(sh?.cta) ? sh.cta[0] : sh?.cta;
              const ctaFontSize = rawCta?.fontSize || sh?.fontSize;
              const cta = rawCta ? {
                ...rawCta,
                modalData: Array.isArray(rawCta.modalData) ? rawCta.modalData[0] : rawCta.modalData,
                ...(ctaFontSize && {
                  sx: {
                    ...(rawCta.sx || {}),
                    fontSize: ctaFontSize
                  }
                }),
                ...(rawCta.label?.includes?.("[channel_count]") && {
                  label: rawCta.label.replace(/\[channel_count\]/g, channelCount)
                })
              } : undefined;
              const content = sh?.content?.includes?.("[channel_count]") ?
                sh.content.replace(/\[channel_count\]/g, channelCount) :
                sh?.content;
              return {
                subHeading: {
                  ...sh,
                  ...(content !== sh?.content && { content }),
                  ...(cta && { cta })
                }
              };
            })()),
            ...(card?.backCard?.strikeThroughPrice && {
              strikeThroughPrice: resolveStrikeThrough(card?.backCard?.strikeThroughPrice)
            }),
            ...(card?.backCard?.offerRibbon && {
              offerRibbon: resolveOfferRibbon(card?.backCard?.offerRibbon, backIsDynamic)
            }),
            priceText: splitCentsToSpanText(resolvePriceText(card?.backCard?.priceText)),
            legalText: backLegal,
            images: Array.isArray(card?.backCard?.images) ? card.backCard.images : [card?.backCard?.images].filter(Boolean),
            svods: (() => {
              const dynamic = dynamicSvods?.[cardId]?.svods || [];
              const cms = card?.backCard?.svods || [];
              if (!cms.length) {
                return dynamic;
              }
              const dynamicNames = new Set(dynamic.map((s: any) => s?.name));
              const cmsOnly = cms.filter((s: any) => s?.name && !dynamicNames.has(s.name));
              return [...dynamic, ...cmsOnly];
            })()
          } :
          undefined
      };
    });
  } catch (error) {
    logError(ADAPTER_NAME, error, { step: "injectDynamicContent" });
    return cards;
  }
};

export const flipCardsAdapter = (args: IFlipCardsAdapter) => {
  try {
    logAdapterEvent(ADAPTER_NAME, "Adapter execution started");

    const { content, packageConfig, genreSvodOffers, embeddedSvods, getOffersData, getOffersDTVsData, dtviEmbeddedSvodOffers, dtvsEmbeddedSvodOffers, byodPlanCardOffers } = args || {};

    if (!content) {
      logError(ADAPTER_NAME, new Error("Missing content"), { step: "flipCardsAdapter" });
      return { props: { ...FlipCardsCmsData } };
    }

    // Derive productType from container config (array or object)
    const containerConfig = Array.isArray(content?.config) ?
      content.config[0] :
      content?.config;
    const productType = normalizeProductType(containerConfig?.productType);

    if (!productType) {
      logError(ADAPTER_NAME, new Error("productType is undefined – config may be empty"), {
        step: "flipCardsAdapter",
        config: content?.config
      });
    }

    // Inject dynamic content into cards
    const enrichedCards = Array.isArray(content?.cards) ?
      injectDynamicContent({
        cards: content.cards,
        packageConfig,
        genreSvodOffers,
        getOffersData,
        getOffersDTVsData,
        embeddedSvods,
        productType,
        dtviEmbeddedSvodOffers,
        dtvsEmbeddedSvodOffers,
        byodPlanCardOffers
      }) :
      [];

    const comparePkgTrayData = content?.comparePkgTray && packageConfig ?
      getComparePkgTrayData({
        comparePkgTray: content.comparePkgTray,
        packageConfig,
        genreSvodOffers,
        getOffersData
      }) :
      null;
    return {
      props: {
        ...content,
        cards: enrichedCards,
        comparePkgTray: comparePkgTrayData || content?.comparePkgTray || null
      },
      type: content?.type
    };
  } catch (error) {
    logError(ADAPTER_NAME, error);
    return {
      props: { ...FlipCardsCmsData }
    };
  }
};
